import apiClient, { refreshAuthToken } from '../../config/api';

// ============================================================================
// Helpers
// ============================================================================

/**
 * Attempts to extract the "answer" value from a string that looks like JSON
 * but may contain unescaped newlines (invalid JSON).
 */
function extractAnswerFromJson(raw: string): string | null {
  // Try strict JSON parse first
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.answer === 'string') return parsed.answer;
  } catch { /* continue to regex fallback */ }

  // Regex fallback: extract content between "answer": " and the trailing " }
  const match = raw.match(/\{\s*"answer"\s*:\s*"([\s\S]*?)"\s*\}$/);
  if (match) return match[1].replace(/\\n/g, '\n').replace(/\\"/g, '"');

  return null;
}

// ============================================================================
// Types
// ============================================================================

export interface FAQAssistanceRequest {
  question: string;
  lang_code?: string;
  userId?: string;
}

export interface FAQAssistanceSource {
  question?: string;
  answer?: string;
  score?: number;
  [key: string]: any;
}

export interface FAQStreamEvent {
  type: 'chunk' | 'sources' | 'done' | 'restore' | 'error';
  content?: string;
  sources?: FAQAssistanceSource[];
  rejected?: boolean;
  motivo?: string;
  error?: string;
}

// ============================================================================
// FAQ Assistance Service
// ============================================================================

export class FAQAssistanceService {
  /**
   * Stream a FAQ answer via SSE. Calls onChunk for each text delta,
   * onSources when sources arrive, and onDone when complete.
   */
  async askQuestionStream(
    question: string,
    callbacks: {
      onChunk: (text: string) => void;
      onSources?: (sources: FAQAssistanceSource[]) => void;
      onDone?: (rejected?: boolean) => void;
      onError?: (error: Error) => void;
      onRestore?: (motivo?: string) => void;
    },
    userId?: string,
    langCode: string = 'es-ES',
  ): Promise<void> {
    const baseURL = apiClient.defaults.baseURL || '';
    const url = `${baseURL}/faq-assistance`;

    const requestBody: FAQAssistanceRequest = {
      question,
      lang_code: langCode,
      userId: userId,
    };

    try {
      let response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this._getAuthHeaders(),
        },
        body: JSON.stringify(requestBody),
      });

      // Token expired – refresh and retry once
      if (response.status === 401) {
        const newToken = await refreshAuthToken();
        response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${newToken}`,
          },
          body: JSON.stringify(requestBody),
        });
      }

      if (!response.ok) {
        throw new Error(`FAQ stream error: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No readable stream');

      const decoder = new TextDecoder();
      let buffer = '';
      let fullResponse = '';
      let hasProcessedEvents = false;

      // JSON-wrapper stripping state (streams progressively)
      const JSON_PREFIX = /^\{\s*"answer"\s*:\s*"/;
      const JSON_SUFFIX_RE = /"\s*\}\s*$/;
      let contentBuffer = '';
      let isJsonWrapped: boolean | null = null; // null = undecided
      let hasFlushedContent = false;
      let streamTerminal = false;
      const DETECT_THRESHOLD = 15; // enough chars to detect {"answer":"

      const flushBuffer = (final = false) => {
        if (isJsonWrapped === null && contentBuffer.length >= DETECT_THRESHOLD) {
          // Decide if it's JSON-wrapped
          if (JSON_PREFIX.test(contentBuffer)) {
            isJsonWrapped = true;
            contentBuffer = contentBuffer.replace(JSON_PREFIX, '');
          } else {
            isJsonWrapped = false;
          }
        }

        if (isJsonWrapped === null && !final) return; // still buffering

        if (isJsonWrapped === null && final) {
          // Short content — check if it's JSON
          if (JSON_PREFIX.test(contentBuffer)) {
            isJsonWrapped = true;
            contentBuffer = contentBuffer.replace(JSON_PREFIX, '');
          } else {
            isJsonWrapped = false;
          }
        }

        if (final) {
          // Strip trailing "} if JSON-wrapped
          if (isJsonWrapped && JSON_SUFFIX_RE.test(contentBuffer)) {
            contentBuffer = contentBuffer.replace(JSON_SUFFIX_RE, '');
          }
          if (contentBuffer) {
            callbacks.onChunk(contentBuffer);
            hasFlushedContent = true;
          }
          contentBuffer = '';
        } else {
          // Stream progressively but hold back last 3 chars (to catch trailing "})
          const holdBack = isJsonWrapped ? 3 : 0;
          if (contentBuffer.length > holdBack) {
            const toFlush = contentBuffer.slice(0, contentBuffer.length - holdBack);
            contentBuffer = contentBuffer.slice(contentBuffer.length - holdBack);
            callbacks.onChunk(toFlush);
            hasFlushedContent = true;
          }
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        fullResponse += chunk;
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine) continue;

          // Support both SSE format (data: {...}) and raw JSON ({...})
          let jsonStr: string;
          if (trimmedLine.startsWith('data: ')) {
            jsonStr = trimmedLine.slice(6).trim();
          } else if (trimmedLine.startsWith('{')) {
            jsonStr = trimmedLine;
          } else {
            continue;
          }
          if (!jsonStr) continue;

          try {
            const event = JSON.parse(jsonStr);
            hasProcessedEvents = true;
            if (event.type === 'chunk' && event.content) {
              contentBuffer += event.content;
              flushBuffer();
            } else if (event.type === 'sources' && event.sources) {
              callbacks.onSources?.(event.sources);
            } else if (event.type === 'done') {
              flushBuffer(true);
              callbacks.onDone?.(event.rejected);
            } else if (event.type === 'restore') {
              // El gateway reintenta: lo buffered es del intento muerto.
              contentBuffer = '';
              isJsonWrapped = null;
              hasFlushedContent = false;
              callbacks.onRestore?.(event.motivo);
            } else if (event.type === 'error') {
              streamTerminal = true;
              callbacks.onError?.(new Error(event.error || 'Error en el servicio'));
            } else if (event.answer) {
              callbacks.onChunk(event.answer);
              hasFlushedContent = true;
              callbacks.onDone?.();
            }
          } catch {
            // Skip malformed SSE lines
          }
        }
      }

      if (streamTerminal) {
        return;
      }

      // Flush any remaining buffered content (stream ended without a "done" event)
      if (contentBuffer) {
        flushBuffer(true);
      }

      if (!hasProcessedEvents) {
        // No SSE events parsed — try the full response as JSON
        const raw = (fullResponse + buffer).trim();
        const extracted = extractAnswerFromJson(raw);
        if (extracted) {
          callbacks.onChunk(extracted);
        } else if (raw) {
          callbacks.onChunk(raw);
        }
        callbacks.onDone?.();
      } else if (!hasFlushedContent) {
        callbacks.onDone?.();
      }
    } catch (error) {
      callbacks.onError?.(error instanceof Error ? error : new Error(String(error)));
    }
  }

  private _getAuthHeaders(): Record<string, string> {
    const token =
      (apiClient.defaults.headers.common as Record<string, string>)?.['Authorization'] ||
      '';
    if (token) return { Authorization: token };
    // Fallback: read directly from localStorage
    const stored = localStorage.getItem('authToken');
    if (stored) return { Authorization: `Bearer ${stored}` };
    return {};
  }
}

export const faqAssistanceService = new FAQAssistanceService();
